/**
 * SCRUM-364 (DS-026 S2) — Zod schemas for the org-level channel connection
 * HTTP boundary.
 *
 * Endpoints covered:
 *   POST /organizations/:slug/channel/connection  — ProvisionChannelConnection
 *   GET  /organizations/:slug/channel/connection  — GetChannelConnection
 *
 * SECURITY INVARIANT: the GET response NEVER carries credential material.
 * `ChannelConnectionItemSchema` is an explicit safe-field allow-list — no
 * partner secret is stored per-org (the NextPax token is platform-level, in
 * the S1 token cache). The controller asserts the exact key set at test time.
 *
 * BOUNDARY ALIGNMENT (codex F-2): identifier constraints here MATCH the domain
 * VO (`opaque-identifier.ts`, `channel-pm-settings.vo.ts`) so that input
 * accepted by this schema is always accepted by the VO — preventing an uncaught
 * VO throw from producing an HTTP 500 instead of a 400.
 *   - each identifier string: max 128 chars (= OPAQUE_IDENTIFIER_MAX_LENGTH)
 *   - vrboAdvertiserIds: min 1 element when present (VO rejects empty arrays)
 *   - pmSettings object: at least one of the three fields must be present (VO
 *     throws if all are absent)
 */
import { z } from 'zod';
/** Maximum characters per opaque identifier — must match OPAQUE_IDENTIFIER_MAX_LENGTH. */
const OPAQUE_ID_MAX = 128;
/** Maximum number of VRBO advertiser ids — must match MAX_ADVERTISER_IDS. */
const MAX_VRBO_ADVERTISER_IDS = 50;
// ---------------------------------------------------------------------------
// ChannelPmSettings — optional per-channel identifiers (opaque, not secrets)
// ---------------------------------------------------------------------------
export const ChannelPmSettingsSchema = z
    .object({
    bookingComLegalEntityId: z.string().min(1).max(OPAQUE_ID_MAX).optional(),
    vrboAdvertiserIds: z
        .array(z.string().min(1).max(OPAQUE_ID_MAX))
        .min(1)
        .max(MAX_VRBO_ADVERTISER_IDS)
        .optional(),
    airbnbHostId: z.string().min(1).max(OPAQUE_ID_MAX).optional(),
})
    .refine((data) => data.bookingComLegalEntityId !== undefined ||
    data.vrboAdvertiserIds !== undefined ||
    data.airbnbHostId !== undefined, {
    message: 'pmSettings must have at least one of bookingComLegalEntityId, vrboAdvertiserIds, or airbnbHostId',
})
    .optional();
// ---------------------------------------------------------------------------
// POST body — ProvisionChannelConnectionRequest
// ---------------------------------------------------------------------------
/**
 * `displayName` is the only required field — forwarded to NextPax as the
 * property manager display name. `pmSettings` carries optional per-channel
 * identifiers (Booking.com / VRBO / Airbnb). NO propertyManagerCode input
 * (H2 invariant — the code is always derived from the NextPax egress return).
 */
export const ProvisionChannelConnectionRequestSchema = z
    .object({
    displayName: z.string().min(1).max(255),
    pmSettings: ChannelPmSettingsSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// GET response — ChannelConnectionItem (explicit safe-field allow-list)
// ---------------------------------------------------------------------------
export const ChannelConnectionStatusSchema = z.enum([
    'provisioning',
    'active',
    'provisioning_failed',
]);
/**
 * Safe-field allow-list for the GET response. credentialsRef / organizationId
 * are deliberately absent (no credential is stored per-org in ChannelConnection;
 * the NextPax partner secret lives in env-only S1 token cache).
 */
export const ChannelConnectionItemSchema = z.object({
    id: z.string().uuid(),
    propertyManagerCode: z.string().nullable(),
    status: ChannelConnectionStatusSchema,
    pmSettings: ChannelPmSettingsSchema.pipe(z.any()).nullable().optional(),
    version: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// PUT body — UpdateChannelPmSettingsRequest (SCRUM-368, DS-026 S6, plan P6)
// ---------------------------------------------------------------------------
//
// Sets/updates `legalEntityId`/`advertiserIds`/`hostId` on an ALREADY-active
// `ChannelConnection` (`UpdateChannelPmSettingsUseCase`, P2/2, already GREEN).
//
// `vrboAdvertiserIds` is a COMMA-SEPARATED STRING on the wire (CSV), NOT a
// JSON array — DISCRETIONARY (differs from `ChannelPmSettingsSchema` above,
// the S2 PROVISION body, which accepts a JSON array): this PUT is an
// operator-facing edit form, and a single comma-separated text input is the
// simpler admin-UI affordance. The controller splits/trims/filters the CSV
// into a `string[]` BEFORE calling `ChannelPmSettings.parse` — this schema
// validates the CSV STRING's bounds only (non-empty); the per-element
// opaque-identifier rules are enforced downstream by `ChannelPmSettings.parse`
// once the controller has split it.
export const UpdateChannelPmSettingsRequestSchema = z
    .object({
    bookingComLegalEntityId: z.string().min(1).max(OPAQUE_ID_MAX).optional(),
    vrboAdvertiserIds: z.string().min(1).optional(),
    airbnbHostId: z.string().min(1).max(OPAQUE_ID_MAX).optional(),
})
    .strict()
    .refine((data) => data.bookingComLegalEntityId !== undefined ||
    data.vrboAdvertiserIds !== undefined ||
    data.airbnbHostId !== undefined, {
    message: 'At least one of bookingComLegalEntityId, vrboAdvertiserIds, or airbnbHostId must be present',
});
//# sourceMappingURL=channel-connection.js.map